import { useRef, useEffect } from 'react'
import { useTinyMCESettings } from '.'
import { Editor } from '@tinymce/tinymce-react'
import { setupAutographyFontHandling } from 'utils/tinyMCE/updateDynamicText/makeBrowserAgnostic'
import { useRecoilValue } from 'recoil'
import {
  themeAtom,
  dateFormatAtom,
  caseFuneralHomeAtom,
  universalThemeSelector,
  applicationTypeAtom,
} from 'atoms'
import { userAtom } from '@/atoms/_globalAtoms'
import { pageSetLocaleAtom, layoutUsedFontsAtom } from '@/atoms/_editorAtoms'
import { useTranslation } from 'react-i18next'
import { useIsM3Order } from '@/hooks/utility/useIsM3Order'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'

export const TinyMCE = (props) => {
  useEditorProbeLifetime(
    `TinyMCE:${props.probeLabel ?? (props.init?.toolbar === false ? 'form' : props.isVerse ? 'verse' : 'other')}`,
  )
  const {
    value = '',
    init: additionalInit,
    inline,
    setRef,
    caseDetails,
    isVerse = false,
    setToast,
    setTinyMCERefTrigger,
    updateHeight = () => null,
    onCaseDetailTemplateInserted = () => {},
    onNameArrangement,
    plainTextMode = false,
    probeLabel: _probeLabel,
    ...rest
  } = props

  const editorRef = useRef(null)

  const theme = useRecoilValue(themeAtom)
  const dateFormat = useRecoilValue(dateFormatAtom)
  const funeralHomeDetails = useRecoilValue(caseFuneralHomeAtom)
  const selectedTheme = useRecoilValue(universalThemeSelector)
  const user = useRecoilValue(userAtom)
  const pageSetLocale = useRecoilValue(pageSetLocaleAtom)
  const layoutUsedFonts = useRecoilValue(layoutUsedFontsAtom)
  const applicationType = useRecoilValue(applicationTypeAtom)
  // Product-source-derived M3 check — applicationType alone misses master
  // orders opened from MDL that carry M3 products.
  const isM3Order = useIsM3Order()

  const { i18n } = useTranslation()

  const tinyMCEProps = {
    additionalInit,
    inline,
    caseDetails,
    funeralHomeDetails,
    dateFormat,
    theme,
    selectedTheme,
    isVerse,
    setToast,
    updateHeight,
    user,
    pageSetLocale,
    onCaseDetailTemplateInserted,
    onNameArrangement,
    plainTextMode,
    applicationType,
    isM3Order,
  }

  const initConfig = useTinyMCESettings(tinyMCEProps)

  const onInit = (evt, editor) => {
    countEditorProbeRender('TinyMCE.init')
    editorRef.current = editor
    setRef?.(editor)

    editor.customData = { ...tinyMCEProps, usedFonts: layoutUsedFonts }

    try {
      setupAutographyFontHandling(editor)
    } catch (error) {
      console.error('[Autography Fix] Error setting up handler:', error)
    }

    if (editor) setTinyMCERefTrigger?.(Date.now())
  }

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.customData = {
        ...editorRef.current.customData,
        caseDetails,
        funeralHomeDetails,
        dateFormat,
        selectedTheme,
        isVerse,
        user,
        applicationType,
        isM3Order,
        onNameArrangement,
        // Keep this editor's "Used in this layout" dropdown section current.
        // The customFontSelect fetch reads customData.usedFonts lazily on
        // open, so refreshing it here is enough for every mounted instance.
        usedFonts: layoutUsedFonts,
      }
    }
  }, [
    caseDetails,
    funeralHomeDetails,
    dateFormat,
    selectedTheme,
    isVerse,
    user,
    layoutUsedFonts,
    applicationType,
    isM3Order,
    onNameArrangement,
  ])

  return (
    <Editor
      key={i18n.language}
      onInit={onInit}
      tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/5.10.9/tinymce.min.js"
      initialValue={value}
      init={initConfig}
      {...rest}
    />
  )
}

export default TinyMCE
